// src/utils/fcmUtils.js
// Gestiona el registro del token FCM para notificaciones push.
// Se llama una sola vez por sesión desde AuthContext.

import { getMessaging, getToken } from 'firebase/messaging';
import { doc, updateDoc } from 'firebase/firestore';
import { app, db } from '../config/firebase';

// Flag de sesión: evita llamadas repetidas en cada re-render / onAuthStateChanged
let _registeredThisSession = false;

/**
 * Solicita permiso de notificaciones (si aún no se ha concedido) y guarda
 * el token FCM en Firestore.
 *
 * @param {string}  userId               - UID del usuario autenticado
 * @param {boolean} notificationsAccepted - true → ya aceptó antes, no mostrar diálogo
 */
export const registerFCMToken = async (userId, notificationsAccepted = false) => {
  if (_registeredThisSession) return;
  _registeredThisSession = true;

  // Solo funciona en contextos web con soporte de Notification API
  if (typeof window === 'undefined' || !('Notification' in window)) return;

  // Si el usuario ya denegó antes, no insistir
  if (Notification.permission === 'denied') return;

  try {
    let permission = Notification.permission;

    if (permission === 'default') {
      if (notificationsAccepted) {
        // Usuario pre-aceptado (existente o que ya aceptó): solicitar silenciosamente
        permission = await Notification.requestPermission();
      } else {
        // Usuario nuevo o que nunca ha decidido: mostrar diálogo
        permission = await Notification.requestPermission();
      }
    }

    if (permission !== 'granted') return;

    const messaging = getMessaging(app);

    // Registrar el SW manualmente para evitar el error de MIME type en entornos
    // donde /firebase-messaging-sw.js no existe (local, CI, etc.)
    let swReg;
    try {
      swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch {
      // SW no disponible en este entorno — no hay push notifications
      return;
    }

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (token && userId) {
      await updateDoc(doc(db, 'users', userId), {
        fcmToken: token,
        notificationsAccepted: true,
      });
    }
  } catch (err) {
    // No bloquear la app si FCM falla (bloqueadores, navegadores sin soporte…)
    console.warn('[FCM] Token registration failed:', err.message);
  }
};
